import { ConfigConstants } from '../../config/config-constants';
import { TIMERECORDING_TYPE_OF_WORK_CODES } from '../../models/complex/time-recording-entry.model';
import { getUuidV4 } from '../../utilities/generic-utilities';
import { LoadStatus } from '../load-status';
import { NotificationIdState } from '../utilities/notification-id.type';

/**
 * Feature key under which the timesheet slice is registered in the root store
 * (mirrors the 'paceForms' / 'paceOffline' feature keys).
 */
export const TIMESHEET_COLLECTION_FEATURE_KEY = 'timesheetCollection';

/**
 * Serializable timesheet slice.
 *
 * The NgRx store is the source of truth: reducers apply edits (e.g.
 * `submitConfirmationRequested` commits a confirmation's work entries) and an effect then
 * persists the updated slice to the concurrency store through the generic persistence layer,
 * under the single `timesheetSlice` key (see {@link PersistedTimesheetCollectionState}). Storage is read
 * back into the slice only to rehydrate — on init and on sync-completion — not via a liveQuery.
 * State holds the plain
 * `JsonTimesheetModel` shape only (dates as ISO strings) so it satisfies the root store's
 * `strictStateSerializability` runtime check — no `moment` objects or `*Model` class
 * instances live here. Selectors rehydrate to `TimesheetModel` for consumers that need the
 * rich model.
 */
// Slice state deliberately uses `null` instead of the repo's usual `undefined`:
// the root store's strict serializability runtime checks reject explicitly
// undefined values but accept null (convention adopted from develop).
export type TimesheetCollectionState = Readonly<{
    // holds all timesheet. Only one is allowed to be currently open by having "isSubmitted" as false; all others have
    // to be submitted. This needs to be ensured in the reducer.
    timesheets: TimesheetState[];
    status: LoadStatus;
    error: string | null;
    /** outcome of the most recent optimistic current-timesheet write (form integration, start-of-work update) */
    integration: TimesheetCollectionIntegrationResultModel | null;
    /**
     * The timesheet the UI is currently showing, or `null` to follow the newest one.
     *
     * View state, not persisted: it survives neither a reload nor a rehydrate, and the
     * selector falls back to the newest timesheet whenever the id is absent or no longer
     * present in `timesheets`.
     */
    selectedTimesheetId: string | null;
    /**
     * Whether a write has landed in the store that the persist effect has not yet confirmed
     * written to storage.
     *
     * Set by every write, cleared by a successful persist — and a persist writes the whole slice,
     * so one success clears everything outstanding. Left set by a failed persist: those edits are
     * then in the store and nowhere else, which is exactly when they must not be overwritten.
     *
     * What it protects: the rehydrate on sync-completion reads storage back into the slice, and
     * without this a sync landing mid-edit would replace the timesheet the technician is editing
     * with a copy that predates their edits.
     */
    hasUnpersistedEdits: boolean;
}>;

export type TimesheetState = {
    /**
     * Stable identity of this timesheet, assigned once when it is created and kept across
     * every edit and the submit swap. Required — the slice has never persisted outside the
     * azure-ngrx configuration, so there is no id-less legacy shape to read back.
     */
    timesheetId: string;
    startOfWork: string;
    endOfWork: string;
    /**
     * Every work and manual entry of this timesheet, flat and kept sorted by `start`
     * (tie-broken by `end`) as a reducer invariant — see `withSortedEntries`.
     *
     * Flat rather than nested under (or paired with) a notification list, because that grouping is
     * not what the UI renders: a timesheet is one chronological list of rows, and a work row only
     * shows its `qmNum`. The confirmation-level facts a row needs — customer name and whether the
     * confirmation was submitted — sit on the work entries themselves, so no reader has to join
     * two arrays on a composite id. Keeping the sort in the reducer means readers never re-sort.
     */
    entries: TimesheetEntryState[];
    isSubmitted: boolean;
    attendanceType: ConfigConstants.AttendanceType;
    startAddressValue: string;
    endAddressValue: string;
    saveTimestamp: number;
    isRemoteTimesheet: boolean;
};

export type TimesheetEntryState = TimesheetWorkEntryState | TimesheetManualEntryState;

type TimesheetEntryStateBase = {
    /** Stable key for `@for` tracking and for addressing a single row in a write action. */
    entryId: string;
    notificationId: NotificationIdState;
    /**
     * ISO timestamps, one field per bound.
     *
     * The legacy shape carried `startDate` + `startTime` (and `endDate` + `endTime`) holding the
     * same instant twice; rehydration read only `startDate`/`endDate` and ignored the rest.
     * Splitting an instant into the date and time that two UI fields bind to is the row
     * component's job, not the slice's.
     */
    start: string;
    end: string;
    /** `null` when no address has been selected yet. */
    address: string | null;
    addressInOutModel: boolean;
    isTravelBillable: boolean;
};

/** An entry recorded against a notification, originating from a confirmation's time recording. */
export type TimesheetWorkEntryState = TimesheetEntryStateBase & {
    kind: 'work';
    workCode: string;
    activityType: string;
    meansOfTransport: string;
    distance: number;
    afterHourCallOut: boolean;
    /** Customer of the notification this entry is booked against; the work row displays it. */
    customerName: string;
    /**
     * Whether the confirmation this entry came from has been submitted.
     *
     * Stamped onto every entry of the confirmation by the notification upsert, which replaces the
     * whole set at once — so the flag cannot drift between entries of the same notification.
     * Entries whose confirmation is still open are rendered — marked as in progress, so the time
     * they occupy is visible rather than looking like a gap — but they block the timesheet's own
     * submit and are left out of the submit out model.
     */
    isConfirmationSubmitted: boolean;
};

/** An entry the user added by hand, closing a gap in the timesheet. */
export type TimesheetManualEntryState = TimesheetEntryStateBase & {
    kind: 'manual';
    taskType: string;
    /** Set only on the "add travel back" entries; the key is omitted otherwise. */
    travelType?: TIMERECORDING_TYPE_OF_WORK_CODES;
};

/**
 * Field defaults of a freshly created timesheet. Not exported: every timesheet needs its own
 * `timesheetId`, so callers go through {@link createTimesheetState} rather than spreading a
 * shared template and inheriting its id.
 */
const timesheetStateDefaults: Omit<TimesheetState, 'timesheetId'> = {
    attendanceType: ConfigConstants.AttendanceType.FREE,
    endAddressValue: '',
    endOfWork: '',
    entries: [],
    isRemoteTimesheet: false,
    isSubmitted: false,
    saveTimestamp: 0,
    startAddressValue: '',
    startOfWork: '',
};

/**
 * Creates an empty timesheet under the given identity.
 *
 * The id is a parameter rather than generated here so reducers can stay pure — the submit
 * swap receives the new current timesheet's id in the `timesheetSubmitted` payload.
 *
 * @param timesheetId identity of the new timesheet
 * @returns an empty, non-submitted timesheet
 */
export const createTimesheetState = (timesheetId: string): TimesheetState => ({
    ...timesheetStateDefaults,
    timesheetId,
});

export const initialTimesheetCollectionState: TimesheetCollectionState = {
    timesheets: [createTimesheetState(getUuidV4())],
    status: LoadStatus.Idle,
    error: null,
    integration: null,
    selectedTimesheetId: null,
    hasUnpersistedEdits: false,
};

/**
 * Outcome the integrate reducer records for the persist effect. Reducers must
 * not throw (a throwing reducer breaks the store), so the merge validation
 * reports through this field instead: on `error` the merge was skipped and the
 * persist effect settles the requesting caller with that error; `warnings` are
 * non-fatal inconsistencies the effect logs.
 */
export type TimesheetCollectionIntegrationResultModel = {
    correlationId: string;
    /** omitted on success (never explicitly undefined — strict serializability rejects undefined values) */
    error?: string;
    warnings: string[];
};
